/**
 * DebugInfoOverlayContent — inner content of the Debug Info debug tool.
 *
 * Live FPS, entity/mob counts, player position, camera scroll+zoom.
 *
 * Substrate notes (2026-05-12 refurb):
 *   - FPS comes from useFps() (rAF hook) — the canonical FPS source.
 *   - Camera state comes from CameraSystem.getManager().getICamera()
 *     (returns SubstrateCamera post-H10 arc λ.9 Stage C). Falls back
 *     to "n/a" if the service hasn't initialized yet.
 *   - Player + mob counts continue to come through ServiceRegistry,
 *     which still works.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted. DebugHUDHost decides when to mount it (driven by
 * DebugToolsContext + DebugLauncher).
 */

import React, { useEffect, useState } from 'react';
import { CopyButton } from '../CopyButton';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { useFps } from '../../../diagnostics/useFps';

interface Snapshot {
  entities: number;
  mobs: number;
  playerPos: { x: number; y: number } | null;
  camera: { x: number; y: number; zoom: number } | null;
}

const POLL_INTERVAL_MS = 500;

const EMPTY: Snapshot = {
  entities: 0,
  mobs: 0,
  playerPos: null,
  camera: null,
};

export const DebugInfoOverlayContent: React.FC = () => {
  const fps = useFps();
  const [snap, setSnap] = useState<Snapshot>(EMPTY);

  useEffect(() => {
    const update = () => {
      const registry = ServiceRegistry.getInstance();
      const entitySystem = registry.get('EntitySystem', { optional: true });
      const mobSystem = registry.get('MobSystem', { optional: true });
      const cameraSystem = registry.get('CameraSystem', { optional: true });

      const player = entitySystem?.getPlayerManager()?.getPlayer();
      const mobs = mobSystem?.getManager()?.getAllMobs().length ?? 0;
      const cam = cameraSystem?.getManager().getICamera();

      setSnap({
        entities: player ? 1 : 0,
        mobs,
        playerPos: player ? {
          x: Math.round(player.position.x),
          y: Math.round(player.position.y),
        } : null,
        camera: cam ? {
          x: Math.round(cam.scrollX),
          y: Math.round(cam.scrollY),
          zoom: cam.zoom,
        } : null,
      });
    };

    update();
    const id = setInterval(update, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="debug-info-overlay"
      style={{
        color: '#0f0',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.4,
        padding: '4px 6px',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#7af', marginBottom: 2, letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>DEBUG INFO</span>
        <CopyButton getText={() => [
          `DEBUG INFO @ ${new Date().toISOString()}`,
          `FPS:      ${fps}`,
          `Entities: ${snap.entities}`,
          `Mobs:     ${snap.mobs}`,
          `Player:   ${snap.playerPos ? `(${snap.playerPos.x}, ${snap.playerPos.y})` : 'n/a'}`,
          `Camera:   ${snap.camera ? `(${snap.camera.x}, ${snap.camera.y}) zoom=${snap.camera.zoom.toFixed(2)}` : 'n/a'}`,
        ].join('\n')} />
      </div>
      <div>FPS: {fps}</div>
      <div>Entities: {snap.entities}</div>
      <div>Mobs: {snap.mobs}</div>
      <div>
        Player: {snap.playerPos ? `(${snap.playerPos.x}, ${snap.playerPos.y})` : 'n/a'}
      </div>
      <div>
        Cam: {snap.camera
          ? `(${snap.camera.x}, ${snap.camera.y}) Z:${snap.camera.zoom.toFixed(2)}`
          : 'n/a'}
      </div>
    </div>
  );
};
